import * as Sentry from "@sentry/react";
import axios from "axios";
import { isIndexedDBError } from "helpers";
import { errorTypes } from "helpers/constants";

// Initialize Sentry only if DSN is available
if (process.env.REACT_APP_SENTRY_DSN) {
  Sentry.init({
    dsn: process.env.REACT_APP_SENTRY_DSN,
    release: process.env.REACT_APP_VERSION,
    integrations: [
      Sentry.browserTracingIntegration(),
      Sentry.replayIntegration({
        maskAllText: false
      }),
      Sentry.replayCanvasIntegration({
        enableManualSnapshot: true,
        maskAllText: false
      }),
      // Add console logging integration
      Sentry.consoleLoggingIntegration({
        levels: ["log", "error", "warn", "info", "debug"]
      }),
    ],
    // Performance Monitoring
    tracesSampleRate: 1.0, // Capture 100% of transactions
    // Session Replay
    replaysSessionSampleRate: 0.1, // Sample rate for session replay
    replaysOnErrorSampleRate: 1.0, // Sample rate for session replay on errors
    // Enable logging
    _experiments: {
      enableLogs: true,
    },
    // Filter out aborted/cancelled requests before sending to Sentry
    beforeSend(event, hint) {
      const error = hint.originalException || hint.syntheticException;
      
      // Filter out axios cancelled/aborted requests
      if (error && axios.isCancel && axios.isCancel(error)) {
        return null; // Don't send to Sentry
      }
      
      // Filter out errors with "Request aborted" message
      if (error && error.message === "Request aborted") {
        return null; // Don't send to Sentry
      }
      
      // Filter out axios errors that are cancelled (check for __CANCEL__ property)
      if (error && error.isAxiosError && error.__CANCEL__) {
        return null; // Don't send to Sentry
      }
      
      // Filter out errors with code indicating cancellation
      if (error && error.code === "ERR_CANCELED") {
        return null; // Don't send to Sentry
      }
      
      // Filter out IndexedDB errors - these are browser-level issues, particularly on iOS Safari
      // These errors are often transient and not actionable by the application
      // Check both the error object and the event message/values for IndexedDB errors
      if (error && isIndexedDBError(error)) {
        // Mark as handled but don't send to Sentry - these are browser bugs, not application errors
        return null;
      }
      
      // Also check event message and values directly in case error object structure differs
      const eventMessage = event?.message || '';
      const eventValues = event?.exception?.values || [];
      const hasIndexedDBInMessage = eventMessage.toLowerCase().includes('indexed database') || 
                                     eventMessage.toLowerCase().includes('indexeddb');
      const hasIndexedDBInValues = eventValues.some(value => {
        const valueMessage = value?.value || '';
        const valueType = value?.type || '';
        return valueMessage.toLowerCase().includes('indexed database') ||
               valueMessage.toLowerCase().includes('indexeddb') ||
               (valueType === 'UnknownError' && valueMessage.includes('Indexed Database'));
      });
      
      if (hasIndexedDBInMessage || hasIndexedDBInValues) {
        return null; // Don't send IndexedDB errors to Sentry
      }
      
      // Filter out authorize_data_invalid errors - these are expected user validation errors
      // (invalid email/password combinations), not application bugs
      // Check multiple locations where the error type might be stored
      const errorType = error?.response?.data?.error?.type;
      if (errorType === errorTypes.AUTHORIZE_DATA_INVALID) {
        return null; // Don't send to Sentry
      }
      
      // Filter out network errors and timeouts - these are environmental issues (poor connectivity, timeouts)
      // not application bugs, particularly common on mobile devices
      // Check both the error object and the event message for timeout/network errors
      const errorMessage = error?.message || '';
      const errorCode = error?.code || '';
      const isAxiosError = error?.isAxiosError || false;
      
      // Check for timeout errors in error message, event message, or error code
      const timeoutKeywords = ['timeout', 'timeout exceeded', 'econnaborted'];
      const hasTimeoutMessage = timeoutKeywords.some(keyword => 
        errorMessage.toLowerCase().includes(keyword) || 
        eventMessage.toLowerCase().includes(keyword)
      );
      const isTimeoutCode = errorCode === "ECONNABORTED" || errorCode === "ETIMEDOUT";
      
      // Check for network errors
      const isNetworkError = errorMessage === "Network Error" || 
                            errorCode === "ERR_NETWORK" ||
                            eventMessage === "Network Error";
      
      // Filter out timeout errors (check both axios errors and any error with timeout message)
      if (hasTimeoutMessage || isTimeoutCode || (isAxiosError && isTimeoutCode)) {
        return null; // Don't send to Sentry
      }
      
      // Filter out network errors (check both axios errors and any error with network message)
      if (isNetworkError || (isAxiosError && isNetworkError)) {
        return null; // Don't send to Sentry
      }
      
      return event;
    },
    // Set user context when available
    beforeBreadcrumb(breadcrumb) {
      // Filter out IndexedDB errors from console logs - these are browser-level issues
      // Check breadcrumb message and data for IndexedDB error patterns
      const breadcrumbMessage = (breadcrumb?.message || '').toLowerCase();
      const breadcrumbData = breadcrumb?.data || {};
      const breadcrumbDataMessage = (breadcrumbData?.message || '').toLowerCase();
      
      // Check if this breadcrumb is related to IndexedDB errors
      const indexedDBPatterns = [
        'indexed database server',
        'indexed database',
        'indexeddb',
        'connection to indexed database server'
      ];
      
      const isIndexedDBBreadcrumb = indexedDBPatterns.some(pattern => 
        breadcrumbMessage.includes(pattern) || breadcrumbDataMessage.includes(pattern)
      );
      
      // Don't capture IndexedDB error breadcrumbs (console logs)
      if (isIndexedDBBreadcrumb) {
        return null; // Filter out this breadcrumb
      }
      
      return {
        ...breadcrumb,
        timestamp: Date.now() / 1000,
      };
    },
  });
} else {
  console.warn('Sentry DSN not found. Error tracking is disabled.');
}

// Higher-order function to wrap API calls with Sentry monitoring
export const withSentryMonitoring = (apiCall, operation, name) => {
  return async (...args) => {
    return Sentry.startSpan(
      {
        op: `http.client.${operation}`,
        name: name,
      },
      async () => {
        try {
          return await apiCall(...args);
        } catch (error) {
          // Don't track cancelled/aborted requests
          if (error?.isAxiosError && error?.__CANCEL__) {
            throw error;
          }
          
          // Don't track authorize_data_invalid errors - these are expected user validation errors
          const errorType = error?.response?.data?.error?.type;
          if (errorType === errorTypes.AUTHORIZE_DATA_INVALID) {
            throw error;
          }
          
          // Don't track network errors and timeouts
          const errorMessage = error?.message || '';
          const errorCode = error?.code || '';
          const timeoutKeywords = ['timeout', 'timeout exceeded'];
          const hasTimeoutMessage = timeoutKeywords.some(keyword => 
            errorMessage.toLowerCase().includes(keyword)
          );
          const isTimeoutCode = errorCode === "ECONNABORTED" || errorCode === "ETIMEDOUT";
          const isNetworkError = errorMessage === "Network Error" || errorCode === "ERR_NETWORK";
          const isTimeoutError = hasTimeoutMessage || isTimeoutCode;
          
          if (!isNetworkError && !isTimeoutError) {
            Sentry.captureException(error, {
              extra: {
                operation,
                name,
                url: error?.config?.url,
                method: error?.config?.method,
                status: error?.response?.status,
                responseData: error?.response?.data,
                args: args
              }
            });
          }
          throw error;
        }
      }
    );
  };
};

// Higher-order function to wrap component actions with Sentry monitoring
export const withSentryAction = (action, operation, name) => {
  return async (...args) => {
    return Sentry.startSpan(
      {
        op: `ui.action.${operation}`,
        name: name,
      },
      async () => {
        try {
          return await action(...args);
        } catch (error) {
          // Don't track cancelled/aborted axios requests
          if (axios.isCancel && axios.isCancel(error)) {
            throw error;
          }
          if (error?.message === "Request aborted") {
            throw error;
          }
          if (error?.isAxiosError && error?.__CANCEL__) {
            throw error;
          }
          if (error?.code === "ERR_CANCELED") {
            throw error;
          }
          
          // Don't track authorize_data_invalid errors - these are expected user validation errors
          const errorType = error?.response?.data?.error?.type;
          if (errorType === errorTypes.AUTHORIZE_DATA_INVALID) {
            throw error;
          }
          
          // Don't track network errors and timeouts
          const errorMessage = error?.message || '';
          const errorCode = error?.code || '';
          const timeoutKeywords = ['timeout', 'timeout exceeded'];
          const hasTimeoutMessage = timeoutKeywords.some(keyword => 
            errorMessage.toLowerCase().includes(keyword)
          );
          const isTimeoutCode = errorCode === "ECONNABORTED" || errorCode === "ETIMEDOUT";
          const isNetworkError = errorMessage === "Network Error" || errorCode === "ERR_NETWORK";
          const isTimeoutError = hasTimeoutMessage || isTimeoutCode;
          
          if (!isNetworkError && !isTimeoutError) {
            Sentry.captureException(error, {
              extra: {
                operation,
                name,
                component: error?.componentName,
                props: error?.props,
                args: args
              }
            });
          }
          throw error;
        }
      }
    );
  };
};

// Error tracking with enhanced context
export const trackError = (error, context = {}) => {
  if (!error) {
    return;
  }
  
  // Don't track cancelled/aborted axios requests
  if (axios.isCancel && axios.isCancel(error)) {
    return;
  }
  
  if (error.message === "Request aborted") {
    return;
  }
  
  if (error.isAxiosError && error.__CANCEL__) {
    return;
  }
  
  if (error.code === "ERR_CANCELED") {
    return;
  }
  
  // Don't track geolocation permission errors - these are expected user actions, not bugs
  if (error.skipSentry || 
      (error.message && error.message.toLowerCase().includes('location') && 
       (error.message.toLowerCase().includes('permission') || error.message.toLowerCase().includes('turn on')))) {
    return;
  }
  
  // Don't track authorize_data_invalid errors - these are expected user validation errors
  // (invalid email/password combinations), not application bugs
  const errorType = error?.response?.data?.error?.type || context?.type;
  if (errorType === errorTypes.AUTHORIZE_DATA_INVALID) {
    return;
  }
  
  // Don't track IndexedDB errors - these are browser-level issues, particularly on iOS Safari
  // These errors are often transient and not actionable by the application
  if (error && isIndexedDBError(error)) {
    return;
  }
  
  // Don't track network errors and timeouts - these are environmental issues (poor connectivity, timeouts)
  // not application bugs, particularly common on mobile devices
  // Check for timeout/network errors regardless of whether it's an axios error
  const errorMessage = error?.message || '';
  const errorCode = error?.code || '';
  
  // Check for timeout errors in error message or error code
  const timeoutKeywords = ['timeout', 'timeout exceeded'];
  const hasTimeoutMessage = timeoutKeywords.some(keyword => 
    errorMessage.toLowerCase().includes(keyword)
  );
  const isTimeoutCode = errorCode === "ECONNABORTED" || errorCode === "ETIMEDOUT";
  
  // Check for network errors
  const isNetworkError = errorMessage === "Network Error" || errorCode === "ERR_NETWORK";
  
  // Filter out timeout errors (check both axios errors and any error with timeout message)
  if (hasTimeoutMessage || isTimeoutCode || (error?.isAxiosError && isTimeoutCode)) {
    return;
  }
  
  // Filter out network errors (check both axios errors and any error with network message)
  if (isNetworkError || (error?.isAxiosError && isNetworkError)) {
    return;
  }
  
  // Ensure error is an Error instance for Sentry
  if (!(error instanceof Error)) {
    // Convert plain objects or other types to Error instance
    const errorMessage = error.message || error.toString() || 'Unknown error';
    const convertedError = new Error(errorMessage);
    
    // Preserve original error properties in the new Error instance
    if (error.stack) {
      convertedError.stack = error.stack;
    }
    if (error.name) {
      convertedError.name = error.name;
    }
    
    // Store original error in context for debugging
    context.originalError = error;
    
    // Use converted error for the rest of the function
    error = convertedError;
  }
  
  const { logger } = Sentry;
  
  // Log the error with additional context
  logger.error(logger.fmt`Error occurred: ${error.message}`, {
    ...context,
    stack: error.stack,
    name: error.name
  });

  Sentry.captureException(error, {
    extra: context,
    tags: {
      errorType: error.name,
      ...context.tags
    }
  });
};

export default Sentry; 